import { deliveryAddressLabel } from "./deliveryAllocations";
import { compareTrips, tripNumberLabel } from "./tripNumber";
import React, { useContext, useEffect, useEffect as useReactEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { DeliveryTrip, Product, ProductOrder } from "../../../../data/entities";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledButton } from "../../../../shared/styled/TitleBar";
import { CircularProgress, ListItem, ListItemText, MenuItem, TableRow, TextField } from "@mui/material";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import TextBox from "../../../../shared/components/TextBox";
import IconButton from "../../../../shared/components/IconButton";
import { Add, Edit } from "@mui/icons-material";
import ExportDeliveryTripModal from "./ImportandExportModal/ExportDeliveryTripModal";
import { ContentWrapper, ProductButtonRow, ProductTableText } from "../../../ProductOrder/single/Styled";
import { AddDeliveryTripModal } from "./AddDeliveryTripModal";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { DELIVERY_TRIP_STATUS, ROLE, SALE_ORDER_STATUS } from "../../../../data/enums";
import apiMethods from "../../../../utils/api";
import { error, success } from "../../../../utils/toast";
import CustomDropdownStatus from "../../../../shared/components/CustomDropdownStatus";
import CancelModal from "../../../../shared/components/CancelModal";
import ReturnQuantityModal from "./ReturnQuantityModal";
import { AddTitle, EditTitle } from "../../../../configs/deliveryTrip";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import dayjs from "dayjs";
import Button from "../../../../shared/ui/shadcn/button";
import ImportDeliveryTripModal from "./ImportandExportModal/ImportDeliveryTripModal";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { getDeliveryTripStatusMenu, getDeliveryTripStatusValidationError } from "./deliveryTripStatus";
import DeliveryTripHistoryBadge from "./DeliveryTripHistoryBadge";
import { ProductDeliverySummary } from "./ProductDeliverySummary";

export const AddDeliveryTripTableColumns = [
    { text: "Địa chỉ giao hàng", icon: <></> },
    { text: "Tên sản phẩm", icon: <></> },
    { text: "Số lượng giao", icon: <></> },
    { text: "Số lượng trả lại", icon: <></> },
    { text: "", icon: <></> },
];

export const DeliveryTripTableColumns = [
    { text: "Biển số xe", icon: <></> },
    { text: "Ngày giao", icon: <></> },
    { text: "Chuyến", icon: <></> },
    { text: "Địa chỉ giao hàng", icon: <></> },
    { text: "Trạng thái", icon: <></> },
    { text: "Sản phẩm", icon: <></> },
    { text: "Ghi chú", icon: <></> },
    { text: "Thao tác", icon: <></> },
];


const getDeliveryTripTableColumns = (canViewDetails: boolean) => {
    if (!canViewDetails) {
        return DeliveryTripTableColumns.filter((item) => !["Ghi chú"].includes(item.text));
    }
    return DeliveryTripTableColumns;
};

interface DeliveryTripRowProps {
    item: any;
    idx: number;
    extraRowProps: any;
}

export const AddDeliveryTripModalRow = ({ item, extraRowProps }: DeliveryTripRowProps) => {
    const [manualAddress, setManualAddress] = useState(false);
    const addresses = extraRowProps.deliveryAddresses || [];
    const savedAddressIndex = addresses.findIndex((address: any) => address.address === item.delivery_address);
    const isManual = manualAddress || Boolean(item.delivery_address && savedAddressIndex < 0);
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canEdit = [ROLE.MANAGER, ROLE.PRODUCTION_STAFF, ROLE.LOGISTIC_MANAGER].includes(role);

    const handleFieldChange = (field: string, value: any) => {
        const updatedItem = {
            ...item,
            [field]: value,
        };
        extraRowProps.updateItem(updatedItem);
    };

    return (
        <TableRow key={item.rowKey || item.product_id || item.material_id}>
            <StyledTableCell align="left" style={{minWidth: 220}}>
                <TextField
                    select
                    id={`delivery-address-${item.rowKey}`}
                    SelectProps={{ inputProps: { "aria-label": "Địa chỉ giao hàng" } }}
                    size="small"
                    fullWidth
                    value={isManual ? "other" : savedAddressIndex >= 0 ? String(savedAddressIndex) : ""}
                    onChange={event => {
                        const value = event.target.value;
                        setManualAddress(value === "other");
                        handleFieldChange("delivery_address", value === "other" ? "" : addresses[Number(value)].address);
                    }}
                    disabled={!extraRowProps.canEdit}
                >
                    {addresses.map((address: any, index: number) => (
                        <MenuItem key={address.id || index} value={String(index)} sx={{ whiteSpace: "normal" }}>
                            {address.label} — {address.address}
                        </MenuItem>
                    ))}
                    <MenuItem value="other">Nhập địa chỉ khác</MenuItem>
                </TextField>
                {isManual && <TextField
                    size="small"
                    fullWidth
                    multiline
                    inputProps={{ "aria-label": "Địa chỉ giao hàng khác" }}
                    value={item.delivery_address || ""}
                    onChange={event => handleFieldChange("delivery_address", event.target.value)}
                    disabled={!extraRowProps.canEdit}
                    helperText="Chỉ lưu trên chuyến này, không thêm vào danh sách khách hàng."
                />}
            </StyledTableCell>
            <StyledTableCell align="left" className="tw-w-[45%]">
                {item.product?.name || item.material?.name || "Unknown"}
            </StyledTableCell>
            <StyledTableCell align="left" className="tw-w-[45%]">
                <TextBox
                    numeric
                    text={formatInternationalNumber(item.quantity ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        handleFieldChange("quantity", n);
                    }}
                    disabled={!extraRowProps.canEdit}
                />
            </StyledTableCell>
            <StyledTableCell align="left" className="tw-w-[45%]">
                <TextBox
                    numeric
                    text={formatInternationalNumber(item.returned_quantity ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        handleFieldChange("returned_quantity", n);
                    }}
                />
            </StyledTableCell>
            <StyledTableCell align="left" className="tw-w-[10%]">
                <IconButton aria-label="Tách dòng giao hàng" onClick={() => extraRowProps.splitItem(item)} disabled={!canEdit || !extraRowProps.canEdit || extraRowProps.isMutating}>
                    <Add />
                </IconButton>
                <IconButton
                    aria-label="Bỏ dòng giao hàng" onClick={() => extraRowProps.removeItem(item)}
                    disabled={!canEdit || !extraRowProps.canEdit || extraRowProps.isMutating || Boolean(item.returned_quantity)}
                >
                    <DeleteIcon />
                </IconButton>
            </StyledTableCell>
        </TableRow>
    );
};

export const DeliveryTripRow = ({ item, extraRowProps }: DeliveryTripRowProps) => {
    const isMutating = extraRowProps.mutatingTripId === item.id;

    const isTerminalOrder = [
        SALE_ORDER_STATUS.COMPLETED,
        SALE_ORDER_STATUS.CANCELLED,
    ].includes(
        String(extraRowProps?.productOrder?.status || "").toUpperCase() as SALE_ORDER_STATUS,
    );
    const isTerminalTrip = [
        DELIVERY_TRIP_STATUS.RETURNED,
        DELIVERY_TRIP_STATUS.CANCELLED,
    ].includes(String(item?.status || "").toUpperCase() as DELIVERY_TRIP_STATUS);

    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const handleFieldChange = (field: string, value: any) => {
        extraRowProps.updateItem(item.id, field, value);
    };

    const formatDate = (date: string | Date): string => {
        if (!date) return "";
        return dayjs(date).utc().format("YYYY-MM-DD HH:mm");
    };

    // State to store car plate mappings
    const [carPlateCache, setCarPlateCache] = useState<Record<string, string>>({});

    // Function to fetch car plate number by UUID
    const fetchCarPlateNumber = async (uuid: string) => {
        if (carPlateCache[uuid]) {
            return carPlateCache[uuid];
        }

        try {
            const response: any = await apiMethods.get(`/car-plates/?search=`);
            const carPlate = response.find((plate: any) => plate.id === uuid);
            if (carPlate) {
                setCarPlateCache(prev => ({ ...prev, [uuid]: carPlate.plate_number }));
                return carPlate.plate_number;
            }
        } catch (error) {
            console.error("Error fetching car plate:", error);
        }

        return uuid; // Return UUID as fallback
    };

    // Enhanced car plate display function
    const getCarPlateDisplay = (carPlate: string) => {
        if (!carPlate) return "Unknown";

        // If it's a UUID (contains dashes and is long), it's a car plate ID
        if (carPlate.includes("-") && carPlate.length > 20) {
            // Check if we have it cached
            if (carPlateCache[carPlate]) {
                return carPlateCache[carPlate];
            }
            // Return loading placeholder while fetching to prevent UUID flash
            return "...";
        }

        // If it's not a UUID, it's already a plate number
        return carPlate;
    };

    // Effect to fetch car plate numbers for UUIDs
    useEffect(() => {
        const fetchCarPlatesForItem = async () => {
            const carPlate = item.vehicle_plate || item.car_plate;
            if (carPlate && carPlate.includes("-") && carPlate.length > 20) {
                if (!carPlateCache[carPlate]) {
                    await fetchCarPlateNumber(carPlate);
                }
            }
        };

        fetchCarPlatesForItem();
    }, [item.vehicle_plate, item.car_plate, carPlateCache]);

    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showReturnQuantityModal, setShowReturnQuantityModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [hasExported, setHasExported] = useState(
        role === ROLE.FIRST_STORAGE_KEEPER ? (item.is_first_storage_keeper_exported ?? item.is_exported) :
            role === ROLE.SECOND_STORAGE_KEEPER ? (item.is_second_storage_keeper_exported ?? item.is_exported) :
                item.is_exported,
    );
    const [hasImported, setHasImported] = useState(
        role === ROLE.FIRST_STORAGE_KEEPER ? (item.is_first_storage_keeper_imported ?? item.is_imported) :
            role === ROLE.SECOND_STORAGE_KEEPER ? (item.is_second_storage_keeper_imported ?? item.is_imported) :
                item.is_imported,
    );

    useEffect(() => {
        setHasExported(
            role === ROLE.FIRST_STORAGE_KEEPER ? (item.is_first_storage_keeper_exported ?? item.is_exported) :
                role === ROLE.SECOND_STORAGE_KEEPER ? (item.is_second_storage_keeper_exported ?? item.is_exported) :
                    item.is_exported,
        );
        setHasImported(
            role === ROLE.FIRST_STORAGE_KEEPER ? (item.is_first_storage_keeper_imported ?? item.is_imported) :
                role === ROLE.SECOND_STORAGE_KEEPER ? (item.is_second_storage_keeper_imported ?? item.is_imported) :
                    item.is_imported,
        );
    }, [item.is_first_storage_keeper_exported, item.is_second_storage_keeper_exported, item.is_first_storage_keeper_imported, item.is_second_storage_keeper_imported, item.is_exported, item.is_imported, role]);
    const allLineItems: any[] = Array.isArray(item.normalized_items) && item.normalized_items.length > 0
        ? item.normalized_items
        : (item.delivery_products || item.items || []);
    const addressLabel = deliveryAddressLabel(allLineItems, item.delivery_address || "");
    const getStorageOf = (deliveryItem: any): string => String(deliveryItem?.product?.storage || deliveryItem?.material?.storage || "");
    const itemsKho1 = allLineItems.filter((di: any) => /kho\s*1/i.test(getStorageOf(di)));
    const itemsKho2 = allLineItems.filter((di: any) => /kho\s*2/i.test(getStorageOf(di)));
    const hasKho1 = itemsKho1.length > 0;
    const hasKho2 = itemsKho2.length > 0;
    const roleIsFirstKeeper = role === ROLE.FIRST_STORAGE_KEEPER;
    const roleIsSecondKeeper = role === ROLE.SECOND_STORAGE_KEEPER;
    const roleIsManager = role === ROLE.MANAGER;
    const visibleItems = roleIsFirstKeeper ? itemsKho1 : roleIsSecondKeeper ? itemsKho2 : allLineItems;
    const allowedToImportKho1 = itemsKho1.reduce((acc: number, di: any) => acc + Number(di?.returned_quantity || 0), 0) > 0;
    const allowedToImportKho2 = itemsKho2.reduce((acc: number, di: any) => acc + Number(di?.returned_quantity || 0), 0) > 0;
    const allowedToImportAll = allLineItems.reduce((acc: number, di: any) => acc + Number(di?.returned_quantity || 0), 0) > 0;
    const allowedToImportForRole = roleIsFirstKeeper ? allowedToImportKho1 : roleIsSecondKeeper ? allowedToImportKho2 : allowedToImportAll;
    const disableExportForRole =
        hasExported || isTerminalOrder || isTerminalTrip || (roleIsFirstKeeper && !hasKho1) || (roleIsSecondKeeper && !hasKho2);
    const disableImportForRole =
        hasImported || isTerminalOrder || isTerminalTrip || !allowedToImportForRole;

    const handleStatusChange = (value: { text: string; id: string }) => {
        if (extraRowProps?.isView || isTerminalOrder) return;
        const validationError = getDeliveryTripStatusValidationError(item, value.id);
        if (validationError) {
            error("Cập nhật trạng thái chuyến vận chuyển thất bại", validationError);
            return;
        }
        if (value.id === "Declined") {
            setShowCancelModal(true);
        } else {
            handleFieldChange("status", value.id);
        }
    };

    const handleReturnQuantitiesConfirm = async (returnedQuantities: Record<string, number>, newStatus: string) => {
        const updatedProducts = (item.delivery_products || item.items || []).map((deliveryItem: any) => {
            const itemId = deliveryItem.product?.id || deliveryItem.material?.id;
            const requestedReturnQuantity = returnedQuantities[itemId] || 0;

            // Validate that returned quantity doesn't exceed available quantity
            const returnedQuantity = Math.min(requestedReturnQuantity, deliveryItem.quantity);

            return {
                id: itemId,
                quantity: deliveryItem.quantity - returnedQuantity,
                returned_quantity: returnedQuantity,
                product: deliveryItem.product || null,
                material: deliveryItem.material || null,
            };
        });

        const updatedDeliveryTrip = {
            ...item,
            delivery_products: updatedProducts,
            status: newStatus,
        };

        // Find and update the specific delivery trip
        const updatedDeliveryTrips = extraRowProps.selectedDeliveryTrip.map((trip: DeliveryTrip) => {
            if (trip.id === updatedDeliveryTrip.id) {
                return updatedDeliveryTrip;
            }
            return trip;
        });

        // Update the state with the modified delivery trips array
        extraRowProps.mergeDeliveryTrip({ delivery_trips: updatedDeliveryTrips });
    };

    const renderStatus = (first?: boolean, second?: boolean) => {
        const firstDone = !hasKho1 || first;
        const secondDone = !hasKho2 || second;

        if (firstDone && secondDone) return <span title="Hoàn thành">✅</span>;
        if (first || second) return <span title="Một phần">⚠️</span>;
        return <span title="Chưa bắt đầu">❌</span>;
    };

    return (
        <TableRow
            key={item.id}
            className="tw-w-full"
        >
            <StyledTableCell align="left">{getCarPlateDisplay(item.vehicle_plate || item.car_plate)}</StyledTableCell>
            <StyledTableCell align="left">
                <span>{formatDate(item.scheduled_at || item.date) || "N/A"}</span>
            </StyledTableCell>
            <StyledTableCell align="left">{tripNumberLabel(item.trip_number)}</StyledTableCell>
            <StyledTableCell align="left">
                {addressLabel ? (
                    <span className="tw-whitespace-normal tw-break-words">
                        {addressLabel}
                    </span>
                ) : (
                    <span className="tw-text-gray-600 tw-italic">Chưa có địa chỉ</span>
                )}
            </StyledTableCell>
            <StyledTableCell align="left">
                {isMutating ? <CircularProgress size={20} /> : <CustomDropdownStatus
                    menuList={getDeliveryTripStatusMenu(item.status)}
                    selectedStatus={item.status}
                    handleOnclickItem={handleStatusChange}
                    getStatusColorTheme={(status: string) => {
                        if (isTerminalOrder) {
                            return "DISABLE";
                        }
                        switch (status) {
                            case "PENDING":
                                return "INFO";
                            case "IN_TRANSIT":
                                return "WARNING";
                            case DELIVERY_TRIP_STATUS.DELIVERED:
                                return "SUCCESS";
                            case DELIVERY_TRIP_STATUS.RETURNED:
                                return "WARNING";
                            case DELIVERY_TRIP_STATUS.CANCELLED:
                                return "FAIL";
                            default:
                                return "INFO";
                        }
                    }}
                    disabled={Boolean(extraRowProps.isView) || extraRowProps.disabledEdit || isTerminalOrder || isTerminalTrip || isMutating}
                />}
                {!hasExported && <DeliveryTripHistoryBadge trip={item} order={extraRowProps.productOrder} />}
            </StyledTableCell>
            <StyledTableCell align="left">
                {(!allLineItems || allLineItems.length === 0) && extraRowProps?.isTripItemsLoading?.(item.id) ? (
                    <span className="tw-text-gray-500">Đang tải...</span>
                ) : visibleItems.map((deliveryItem: any, index: number) => (
                    <ListItem
                        key={deliveryItem.id || deliveryItem.sale_order_item_id || index}
                        dense
                        disableGutters
                        className="tw-py-0.5 tw-px-0"
                    >
                        <ListItemText
                            primary={
                                <div className="tw-flex tw-items-center tw-gap-2">
                                <span className="tw-mr-2">
                                    {deliveryItem.product?.name || deliveryItem.material?.name || "Unknown"}
                                </span>
                                    <span
                                        className="tw-bg-blue-50 tw-text-blue-700 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium">
                                    Giao: {deliveryItem.quantity}
                                </span>
                                    <span
                                        className="tw-bg-pink-50 tw-text-pink-700 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium">
                                    Trả lại: {deliveryItem.returned_quantity || 0}
                                </span>
                                </div>
                            }
                            secondary={
                                deliveryItem.material?.providers?.length > 0 && (
                                    <div className="tw-text-xs tw-text-gray-600">
                                        Nhà cung
                                        cấp: {deliveryItem.material.providers.map((provider: any) => provider.provider.name).join(", ")}
                                    </div>
                                )
                            }
                            secondaryTypographyProps={{ noWrap: true }}
                        />
                    </ListItem>
                ))}
            </StyledTableCell>
            <StyledTableCell align="left">
                {(() => {
                    const noteText = item.notes || item.note || "";
                    try {
                        const parsed = JSON.parse(noteText);
                        const parts = [];
                        if (parsed.exported_storages && Array.isArray(parsed.exported_storages)) {
                            parts.push(`Kho đã xuất: ${parsed.exported_storages.join(", ")}`);
                        }
                        if (parsed.imported_storages && Array.isArray(parsed.imported_storages)) {
                            parts.push(`Kho đã nhập: ${parsed.imported_storages.join(", ")}`);
                        }
                        if (parts.length > 0) return parts.join(" | ");
                    } catch (e) {
                    }
                    return noteText || <span className="tw-text-gray-600 tw-italic">Không có ghi chú</span>;
                })()}
            </StyledTableCell>
            <StyledTableCell align="left">
                <div className="tw-flex tw-gap-5">
                    {/* Edit/Delete: only Production Staff and Manager, and only when not view-only */}
                    {!Boolean(extraRowProps.isView) && [ROLE.PRODUCTION_STAFF, ROLE.MANAGER, ROLE.LOGISTIC_MANAGER].includes(role) && (
                        <>
                            <IconButton
                                onClick={() => setShowDeleteModal(true)}
                                disabled={extraRowProps.disabledEdit || hasExported || isTerminalOrder || isTerminalTrip || isMutating}
                                sx={{
                                    color: hasExported ? "#9e9e9e" : "#90caf9",
                                    opacity: hasExported ? 0.5 : 1,
                                }}
                            >
                                {isMutating ? <CircularProgress size={18} /> : <DeleteIcon />}
                            </IconButton>
                            <IconButton
                                onClick={() => extraRowProps.editItem(item)}
                                disabled={extraRowProps.disabledEdit || item.status === "DELIVERED" || isTerminalOrder || isTerminalTrip || isMutating}
                                sx={{ color: "#90caf9" }}
                            >
                                <Edit />
                            </IconButton>
                        </>
                    )}

                    {[ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER, ROLE.MANAGER].includes(role) && (
                        <>
                            <Button
                                onClick={() => setIsExportModalOpen(true)}
                                title="Xuất kho"
                                size="sm"
                                className={`tw-ml-1 tw-h-1/3 ${disableExportForRole ? "tw-bg-gray-500 tw-cursor-not-allowed hover:tw-bg-gray-500" : ""}`}
                                disabled={disableExportForRole}
                            >
                                Xuất kho
                            </Button>
                            <Button
                                onClick={() => setIsImportModalOpen(true)}
                                title="Nhập kho"
                                size="sm"
                                className={`tw-ml-1 tw-h-1/3 ${disableImportForRole ? "tw-bg-gray-500 tw-cursor-not-allowed hover:tw-bg-gray-500" : ""}`}
                                disabled={disableImportForRole}
                            >
                                Nhập kho
                            </Button>
                        </>
                    )}
                </div>
            </StyledTableCell>
            <CancelModal
                open={showCancelModal}
                title="Xác nhận hủy chuyến"
                contentText="Bạn có chắc chắn muốn hủy chuyến vận chuyển này không?Lưu ý: Hủy chuyến vận chuyển không ảnh hưởng đến trạng thái đơn hàng."
                handleConfirm={() => {
                    setShowReturnQuantityModal(true);
                    setShowCancelModal(false);
                }}
                handleClose={() => setShowCancelModal(false)}
            />
            <CancelModal
                open={showDeleteModal}
                title="Xác nhận xóa chuyến"
                contentText="Bạn có chắc chắn muốn xóa chuyến vận chuyển này không? Hành động này không thể hoàn tác."
                handleConfirm={() => {
                    extraRowProps.removeItem(item);
                    setShowDeleteModal(false);
                }}
                handleClose={() => setShowDeleteModal(false)}
                isLoading={isMutating}
            />
            <ReturnQuantityModal
                open={showReturnQuantityModal}
                onClose={() => setShowReturnQuantityModal(false)}
                onConfirm={handleReturnQuantitiesConfirm}
                products={(item.delivery_products || item.items || []).map((deliveryItem: any) => ({
                    id: deliveryItem.product?.id || deliveryItem.material?.id,
                    name: deliveryItem.product?.name || deliveryItem.material?.name || "Unknown",
                    quantity: deliveryItem.quantity,
                    returned_quantity: deliveryItem.returned_quantity || 0,
                }))}
            />
            {isExportModalOpen && (
                <ExportDeliveryTripModal
                    key={(item as any)?.id || (item as any)?.code || "export-modal"}
                    open={isExportModalOpen}
                    handleClose={() => setIsExportModalOpen(false)}
                    onExported={() => {
                        setHasExported(true);
                        const field = role === ROLE.FIRST_STORAGE_KEEPER
                            ? "is_first_storage_keeper_exported"
                            : role === ROLE.SECOND_STORAGE_KEEPER
                                ? "is_second_storage_keeper_exported"
                                : "is_exported";
                        handleFieldChange(field, true);
                    }}
                    deliveryTripId={item.id}
                    initialFormData={{
                        storage: roleIsFirstKeeper ? "Kho 1" : roleIsSecondKeeper ? "Kho 2" : "",
                        customer: extraRowProps?.productOrder?.customer || null,
                        address: item.delivery_address || "",
                        reason: "Xuất bán",
                    }}
                    initialItems={(roleIsFirstKeeper ? itemsKho1 : roleIsSecondKeeper ? itemsKho2 : allLineItems)
                        .filter((deliveryItem: any) => !!deliveryItem.product || !!deliveryItem.material)
                        .map((deliveryItem: any) => {
                            const isProduct = !!deliveryItem.product;
                            const entity = isProduct ? deliveryItem.product : deliveryItem.material;
                            return {
                                id: entity.id,
                                name: entity.name,
                                product_code: isProduct ? entity.product_code : undefined,
                                material_code: !isProduct ? entity.material_code : undefined,
                                requiredQuantity: Number(deliveryItem.quantity ?? 0),
                                exportQuantity: Number(
                                    deliveryItem.exportedQuantity ?? deliveryItem.quantity ?? 0,
                                ),
                                item_type: isProduct ? "product" : "material",
                            };
                        })}
                />
            )}
            {isImportModalOpen && (
                <ImportDeliveryTripModal
                    open={isImportModalOpen}
                    handleClose={() => setIsImportModalOpen(false)}
                    onImported={() => {
                        setHasImported(true);
                        const field = role === ROLE.FIRST_STORAGE_KEEPER
                            ? "is_first_storage_keeper_imported"
                            : role === ROLE.SECOND_STORAGE_KEEPER
                                ? "is_second_storage_keeper_imported"
                                : "is_imported";
                        handleFieldChange(field, true);
                    }}
                    deliveryTripId={item.id}
                    initialFormData={{
                        customer: extraRowProps?.productOrder?.customer.id || null,
                        import_method: "nhập hàng trả lại",
                        storage: roleIsFirstKeeper ? "Kho 1" : roleIsSecondKeeper ? "Kho 2" : "",
                    }}
                    initialItems={(roleIsFirstKeeper ? itemsKho1 : roleIsSecondKeeper ? itemsKho2 : allLineItems)
                        .filter((deliveryItem: any) => (deliveryItem.product || deliveryItem.material) && Number(deliveryItem.returned_quantity) > 0)
                        .map((deliveryItem: any) => {
                            const isProduct = !!deliveryItem.product;
                            const entity = isProduct ? deliveryItem.product : deliveryItem.material;
                            return {
                                id: entity.id,
                                name: entity.name,
                                requiredQuantity: Number(deliveryItem.returned_quantity ?? 0),
                                importQuantity: Number(
                                    deliveryItem.returned_quantity ?? 0,
                                ),
                                product_code: isProduct ? entity.product_code : undefined,
                                material_code: !isProduct ? entity.material_code : undefined,
                                item_type: isProduct ? "product" : "material",
                            };
                        })}
                />
            )}
        </TableRow>
    );
};

interface DeliveryTripListProps {
    deliveryTrips: DeliveryTrip[];
    mergeDeliveryTrip: Function;
    fetchProductOrder?: () => Promise<void>;
    isView?: boolean;
    products: Product[];
    productOrder: ProductOrder;
    disabledEdit?: boolean;
}

const DeliveryTripList = ({
                              deliveryTrips,
                              mergeDeliveryTrip,
                              fetchProductOrder,
                              isView,
                              productOrder,
                              disabledEdit,
                          }: DeliveryTripListProps) => {
    const { productOrderId } = useParams();

    const isTerminalOrder = [
        SALE_ORDER_STATUS.COMPLETED,
        SALE_ORDER_STATUS.CANCELLED,
    ].includes(String(productOrder?.status || "").toUpperCase() as SALE_ORDER_STATUS);


    const [isDeliveryTripModalOpen, setIsDeliveryTripModalOpen] = useState(false);
    const [mutatingTripId, setMutatingTripId] = useState<string | null>(null);
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canEdit = [ROLE.MANAGER, ROLE.PRODUCTION_STAFF, ROLE.LOGISTIC_MANAGER].includes(role);

    const onRemoveDeliveryTrip = async (toRemoveItem: DeliveryTrip) => {
        if (isView || isTerminalOrder || mutatingTripId) return;
        const tripMutationId = toRemoveItem.id || "local";
        setMutatingTripId(tripMutationId);

        try {
            // If it's an existing trip (has ID), delete from backend immediately
            if (toRemoveItem.id) {
                await apiMethods.delete(`/product-flow/v2/delivery-trips/${toRemoveItem.id}/`);
                success("THÀNH CÔNG", "Xóa chuyến vận chuyển thành công");
            }

            const updatedDeliveryTrips = deliveryTrips.filter(
                (trip) => trip.id !== toRemoveItem.id,
            );

            mergeDeliveryTrip({ delivery_trips: updatedDeliveryTrips });
            await fetchProductOrder?.();
        } catch (err: any) {
            console.error("Error deleting delivery trip:", err);
            error("Lỗi xóa chuyến vận chuyển", err);
        } finally {
            setMutatingTripId(null);
        }
    };

    const onUpdateDeliveryTripField = async (id: string, field: string, value: any) => {
        if (isView || isTerminalOrder) return;

        if (field === "status") {
            if (mutatingTripId) return;
            setMutatingTripId(id);
            try {
                const payload: any = { status: value };
                if (value === "IN_TRANSIT") payload.departure_at = dayjs().toISOString();
                if (value === "DELIVERED") payload.arrived_at = dayjs().toISOString();
                await apiMethods.patch(`/product-flow/v2/delivery-trips/${id}/status/`, payload);
                success("THÀNH CÔNG", "Cập nhật trạng thái chuyến vận chuyển thành công");
            } catch (err) {
                error("Cập nhật trạng thái chuyến vận chuyển thất bại", err as any);
                return;
            } finally {
                setMutatingTripId(null);
            }
        }

        const updatedDeliveryTrips = deliveryTrips.map((trip) => {
            if (trip.id === id) {
                return { ...trip, [field]: value };
            }
            return trip;
        });

        mergeDeliveryTrip({ delivery_trips: updatedDeliveryTrips });
        await fetchProductOrder?.();
    };
    const [selectedDeliveryTrip, setSelectedDeliveryTrip] = useState<DeliveryTrip | null>(null);

    const onEditDeliveryTrip = async (deliveryTrip: DeliveryTrip) => {
        if (isView || isTerminalOrder || !deliveryTrip.id) return;
        setLoadingTripItems(prev => ({...prev, [deliveryTrip.id!]: true}));
        try {
            const response: any = await apiMethods.get(`/product-flow/v2/delivery-trips/${deliveryTrip.id}/items/`);
            const normalized = normalizeTripItems(Array.isArray(response) ? response : response?.results || []);
            setTripItemsById(prev => ({...prev, [deliveryTrip.id!]: normalized}));
            setSelectedDeliveryTrip({...deliveryTrip, normalized_items: normalized} as DeliveryTrip);
            setIsDeliveryTripModalOpen(true);
        } catch (err) {
            error("Không tải được sản phẩm của chuyến", err);
        } finally {
            setLoadingTripItems(prev => ({...prev, [deliveryTrip.id!]: false}));
        }
    };

    const [tripItemsById, setTripItemsById] = useState<Record<string, any[]>>({});
    const [loadingTripItems, setLoadingTripItems] = useState<Record<string, boolean>>({});

    const normalizeTripItems = useMemo(() => {
        const saleOrderItems: any[] = Array.isArray((productOrder as any)?.items)
            ? (productOrder as any).items
            : [];
        const soItemById: Record<string, any> = Object.fromEntries(
            saleOrderItems.map((it: any) => [String(it?.id || ""), it]),
        );
        return (rawItems: any[]): any[] => {
            return (rawItems || []).map((it: any) => {
                const lineId = String(it?.sale_order_item_id || "");
                const soItem = lineId ? soItemById[lineId] : undefined;
                if (soItem) {
                    const prod = soItem.product ? { ...soItem.product } : undefined;
                    const mat = soItem.material ? { ...soItem.material } : undefined;
                    return {
                        ...it,
                        product: prod || it.product || undefined,
                        material: mat || it.material || undefined,
                    };
                }
                return it;
            });
        };
    }, [productOrder]);

    useEffect(() => {
        const next: Record<string, any[]> = { ...tripItemsById };
        let changed = false;
        (deliveryTrips || []).forEach((t: any) => {
            if (!t?.id) return;
            if (Array.isArray(t.normalized_items)) {
                next[t.id] = normalizeTripItems(t.normalized_items);
                changed = true;
                return;
            }
            const embedded = (t.normalized_items || t.delivery_products || t.items || []) as any[];
            if (embedded.length > 0 && !next[t.id]) {
                next[t.id] = normalizeTripItems(embedded);
                changed = true;
            }
        });
        if (changed) {
            setTripItemsById(next);
        }
    }, [deliveryTrips, normalizeTripItems]);

    useReactEffect(() => {
        let cancelled = false;
        const trips = (deliveryTrips || []).filter((t: any) => !!t?.id);
        const needFetch = trips.filter((t: any) => !tripItemsById[t.id]);
        if (needFetch.length === 0) return;

        const doFetch = async () => {
            for (const t of needFetch) {
                const id = t.id as string;
                setLoadingTripItems((prev) => ({ ...prev, [id]: true }));
                try {
                    const resp: any = await apiMethods.get(`/product-flow/v2/delivery-trips/${id}/items/`);
                    if (cancelled) return;
                    const raw = Array.isArray(resp) ? resp : (resp?.results || []);
                    const normalized = normalizeTripItems(raw);
                    setTripItemsById((prev) => ({ ...prev, [id]: normalized }));
                } catch (_e) {
                    // keep cache empty on error
                } finally {
                    if (!cancelled) setLoadingTripItems((prev) => ({ ...prev, [id]: false }));
                }
            }
        };
        doFetch();
        return () => {
            cancelled = true;
        };
    }, [deliveryTrips, productOrder?.id]);

    const viewTrips = useMemo(() => {
        return (deliveryTrips || []).map((t: any) => ({
            ...t,
            normalized_items: tripItemsById[t?.id || ""] || [],
        }));
    }, [deliveryTrips, tripItemsById]);

    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const id = searchParams.get("deliveryTrip");
        const trip = viewTrips.find((item: any) => item.id === id);
        if (!trip || !Object.prototype.hasOwnProperty.call(tripItemsById, id || "") || !canEdit || isView || isTerminalOrder) return;
        setSelectedDeliveryTrip(trip);
        setIsDeliveryTripModalOpen(true);
        const next = new URLSearchParams(searchParams); next.delete("deliveryTrip"); setSearchParams(next, { replace: true });
    }, [searchParams, viewTrips, canEdit, isView, isTerminalOrder, setSearchParams]);

    return (
        <>
            {isDeliveryTripModalOpen && !isView && !isTerminalOrder && (
                <AddDeliveryTripModal
                    title={selectedDeliveryTrip ? EditTitle.title : AddTitle.title}
                    selectedDeliveryTrip={selectedDeliveryTrip || {} as DeliveryTrip}
                    open={isDeliveryTripModalOpen}
                    handleClose={() => setIsDeliveryTripModalOpen(false)}
                    mergeDeliveryTrip={mergeDeliveryTrip}
                    fetchProductOrder={fetchProductOrder}
                    productOrder={productOrder}
                />
            )}

            <ContentWrapper>
                <ProductDeliverySummary products={productOrder.products || []} />
                <div className="tw-flex tw-justify-between tw-items-center">
                    <ProductTableText className="tw-w-full">Danh sách chuyến vận chuyển</ProductTableText>
                </div>

                <CustomTable
                    itemList={[...(viewTrips || [])].sort(compareTrips)}
                    RowElement={DeliveryTripRow}
                    TableColumns={getDeliveryTripTableColumns(true)}
                    extraRowProps={{
                        selectedDeliveryTrip: viewTrips,
                        productOrder: productOrder,
                        isView: Boolean(isView),
                        removeItem: onRemoveDeliveryTrip,
                        updateItem: onUpdateDeliveryTripField,
                        editItem: onEditDeliveryTrip,
                        mergeDeliveryTrip,
                        disabledEdit,
                        mutatingTripId,
                        isTripItemsLoading: (tripId: string) => Boolean(loadingTripItems[tripId]),
                    }}
                />

                {viewTrips.length === 0 && "Không có chuyến vận chuyển được chọn"}
                {!isView && canEdit && (
                    <ProductButtonRow>
                        <StyledButton
                            variant="contained"
                            onClick={() => {
                                setSelectedDeliveryTrip(null);
                                setIsDeliveryTripModalOpen(true);
                            }}
                            disabled={disabledEdit || isTerminalOrder}
                        >
                            Thêm chuyến vận chuyển
                        </StyledButton>
                    </ProductButtonRow>
                )}

            </ContentWrapper>
        </>
    );
};

export default DeliveryTripList;
